// Design Mode Product Selection Sidebar utilities
// being the utils for the flow of clicking rpoducts in the sidebar when designing

import constants from 'src/constants';
import featureFlags from 'src/featureFlags';
import macheteUtils from 'src/utils/macheteUtils';
import productCatalogUtils from 'src/utils/productCatalogUtils';

const { BRICK_ID } = constants.labels;

/**
 * Helper to create a node style update. When a given property is updated,
 * this helper also erases the properties that depended on the property being
 * changed. For example if material is changed, all other properties are
 * erased. If product is changed, color is erased.
 *
 * materialTextureId and MaterialColorHex are only used to show a specific
 * texture before the user has changed anything. This is because in the job's
 * manowar data, the texture id isn't the default texture for the primary label
 * id
 */

/*
 * @param {string} attribute
 * @param {string} value
 * @return {Object}
 */

const generateStyleUpdate = (attribute, value) => {
  const update = { [attribute]: value };
  switch (attribute) {
    case 'materialId':
      return {
        ...update,
        brandId: '',
        productId: '',
        colorId: '',
        materialTextureId: '',
        materialColorHex: '',
        frameType: '',
        gridType: '',
      };
    case 'brandId':
      return {
        ...update,
        productId: '',
        colorId: '',
      };
    case 'productId':
      return {
        ...update,
        colorId: '',
        materialTextureId: '',
        materialColorHex: '',
      };
    default:
      return update;
  }
};

const clearPaintUpdate = {
  paintBrandId: '',
  paintProductId: '',
  paintColorId: '',
};

/*
 * @param {Object} activeAttribute
 * @param {Object} products
 * @param {Object} selectedProduct
 * @return {Object}
 */
const generatePaintColorUpdate = (
  activeAttribute,
  products,
  selectedProduct,
) => {
  let paintColorUpdate = {};

  if (activeAttribute.property === 'paintBrandId') {
    const product = Object.values(products).filter(({ manufacturer_id }) => {
      return manufacturer_id === selectedProduct.id;
    })[0];
    paintColorUpdate = generateStyleUpdate('paintProductId', product.id);
  } else if (activeAttribute.property === 'materialId') {
    // remove paint when changing to unpaintable material
    if (!productCatalogUtils.isPaintableMaterial(selectedProduct.id)) {
      paintColorUpdate = clearPaintUpdate;
    }
  } else if (
    activeAttribute.property === 'productId' &&
    selectedProduct.label_id === BRICK_ID &&
    !productCatalogUtils.isPaintableBrickProduct(selectedProduct.id)
  ) {
    paintColorUpdate = clearPaintUpdate;
  }
  return paintColorUpdate;
};

/*
 * @param {Object} manufacturerProducts
 * @param {Object} activeStyle
 * @param {Object} activeAttribute
 * @param {Object} texture
 * @return {Object}
 */
const generateDoorOrWindowUpdate = (manufacturerProducts, texture) => {
  const windowData = {};
  // get product data from window item parent_id
  const productData = manufacturerProducts[texture.parent_id] || {};
  // get product group data from product parent_id
  const groupData = manufacturerProducts[productData.parent_id];

  /**
   * TODO: Update legacy windows code once we can store extra attributes such as
   * frameType and gridType on varations instead of inferring it from the
   * grouping name.
   *
   * NOTE: groupData is null unless varation is part of a grouping. Guarding it
   * in an if block stops the app from crashing when a product is added that
   * isn't part of a grouping
   */
  if (groupData) {
    // get grid type from product name
    windowData.gridType = productData.name.replace(/\s/g, '').toLowerCase();
    // get frame type from product parent name
    windowData.frameType = groupData.name.replace(/\s/g, '').toLowerCase();
  }

  return windowData;
};

/**
 * Thumbnails for products/materials come from Planet Terror
 * Thumbnails Partner logos come from the assets folder
 * all assets from the assets folder need to use require for webpack
 * And since the components that use this function will be handing both
 * we have to handel them differently. This function returns the correct
 * url for each case.
 */

/*
 * @param {string} imageUrl
 * @return {string} rendering URL
 */
const generateThumbnailURL = (imageUrl) => {
  // And since assets come from both Product Catalog and local we have to differentiate
  let urlPath = imageUrl;
  // require needs part of the path to correctly build local assets
  // https://vuejs-templates.github.io/webpack/static.html
  if (imageUrl && imageUrl.includes('partner_logos/')) {
    urlPath = require(`src/assets/images/${imageUrl}`);
  }

  return urlPath;
};

const isBrandURL = (imageUrl) => {
  return imageUrl && imageUrl.includes('partner_logos/');
};

/*
 * Some products, manufacturers, or variations need to be
 * hidden from the Design Sidebar. Sometimes this is because of
 * business reasons. For example, we needed to hide the Generic HOVER
 * Metal Roof product in order to sign a contract with a metal roof company.
 */
const showInDesignExtendedSidebar = (item) => {
  if (productCatalogUtils.isHoverGenericMetalRoof(item)) {
    return false;
  }
  return true;
};

const shouldShowVariation = (variation) => {
  if (productCatalogUtils.isDiscontinuedCertainTeedVariation(variation)) {
    return false;
  }
  if (productCatalogUtils.isDiscontinuedMasticVariation(variation)) {
    return false;
  }
  return true;
};

const shouldShowVariationGroup = (variationGroup) => {
  if (productCatalogUtils.isDiscontinuedGAFVariationGroup(variationGroup)) {
    return false;
  }
  if (
    productCatalogUtils.isDiscontinuedCertainTeedVariationGroup(variationGroup)
  ) {
    return false;
  }
  return true;
};

const shouldShowProduct = (product) => {
  if (productCatalogUtils.isDiscontinuedPlygemProduct(product)) {
    return false;
  }
  if (productCatalogUtils.isDiscontinuedMasticProduct(product)) {
    return false;
  }
  return true;
};

const shouldShowRenderingCTA = (canRequestRendering) =>
  canRequestRendering && featureFlags.shouldShowRenderings();

const shouldShowPaintMenu = (materialId, productId) => {
  if (macheteUtils.isBrick(materialId)) {
    return productCatalogUtils.isPaintableBrickProduct(productId);
  }
  return productCatalogUtils.isPaintableMaterial(materialId);
};

const shouldShowManufacturerForMaterial = (manufacturer, materialId) => {
  return productCatalogUtils.doesBrandProvideMaterial(manufacturer, materialId);
};

export default {
  generatePaintColorUpdate,
  generateStyleUpdate,
  generateThumbnailURL,
  generateDoorOrWindowUpdate,
  shouldShowManufacturerForMaterial,
  shouldShowPaintMenu,
  shouldShowProduct,
  shouldShowVariation,
  shouldShowVariationGroup,
  showInDesignExtendedSidebar,
  shouldShowRenderingCTA,
  isBrandURL,
};
